import { memo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useGameStore } from '../../store/game-store';
import type { Move } from '@draughts/shared';
import { formatMoveNotation } from '@draughts/core';
import { pairMoves } from './move-pairs';
import { useBlindGame } from './blind-context';
import './MoveList.css';

const EMPTY_MOVES: Move[] = [];

interface LocalMoveListProps {
  /**
   * L'id del blocco, per chi lo COMANDA da fuori: e' il bersaglio dell'`aria-controls` del
   * chevron che apre e chiude la tabella (la damiera d'analisi).
   *
   * Prop e non costante interna: `LocalGame` monta questa lista in due punti — la barra
   * laterale e il pannello mobile — e un id fisso rischierebbe un duplicato in pagina.
   * Sta su ENTRAMBI i rami di ritorno, perche' a linea vuota l'`aria-controls` punterebbe
   * al nulla, che e' esso stesso un difetto.
   */
  id?: string;
}

function LocalMoveList({ id }: LocalMoveListProps) {
  const { t } = useTranslation();
  const moveHistory = useGameStore((state) => state.moveHistory ?? EMPTY_MOVES);
  const variant = useGameStore((state) => state.variant);
  const reviewIndex = useGameStore((state) => state.reviewIndex);
  const reviewToIndex = useGameStore((state) => state.reviewToIndex);
  const blind = useBlindGame();
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [moveHistory.length]);

  if (moveHistory.length === 0) {
    return (
      <div id={id} className={`move-list${blind ? ' move-list--blind' : ''}`} data-testid="move-list">
        <div className="move-list-empty">{t('game.sidebar.noMoves', 'No moves yet')}</div>
      </div>
    );
  }

  const pairs = pairMoves(moveHistory);
  // Currently-viewed ply: live => last move; reviewing N => move N-1 produced it.
  const activeMoveIndex = reviewIndex === null ? moveHistory.length - 1 : reviewIndex - 1;

  const handleClick = (plyIndex: number) => reviewToIndex(plyIndex + 1);
  const handleKeyDown = (e: React.KeyboardEvent, plyIndex: number) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick(plyIndex);
    }
  };

  // ALLA CIECA LA LISTA E' UNA LETTURA, NON UNA NAVIGAZIONE.
  // Senza il «vai alla posizione attuale» — che sparisce con le frecce — un tocco su una
  // mossa passata lascerebbe la damiera bloccata in revisione senza via d'uscita
  // visibile: l'unica rimasta sarebbe toccare proprio l'ULTIMA mossa, che lo store
  // rimappa su «live», e non lo indovina nessuno. E non ci sarebbe niente da vedere:
  // sotto la benda anche la posizione rivista e' vuota.
  //
  // Un solo oggetto sparso su tutte e due le celle (e identico nei due file) perche' due
  // copie dello stesso ramo sono due occasioni di divergere.
  const cellProps = (plyIndex: number) =>
    blind
      ? {}
      : {
          role: 'button' as const,
          tabIndex: 0,
          'aria-label': t('game.sidebar.viewMove', 'View position after move {{n}}', { n: plyIndex + 1 }),
          onClick: () => handleClick(plyIndex),
          onKeyDown: (e: React.KeyboardEvent) => handleKeyDown(e, plyIndex),
        };

  return (
    <div id={id} className={`move-list${blind ? ' move-list--blind' : ''}`} data-testid="move-list" ref={scrollRef}>
      <table className="move-list-table">
        <tbody>
          {pairs.map(([white, black], pairIndex) => {
            const whiteIndex = pairIndex * 2;
            const blackIndex = pairIndex * 2 + 1;
            return (
              <tr key={pairIndex} className="move-list-row">
                <td className="move-list-num">{pairIndex + 1}.</td>
                <td
                  className={`move-list-move${whiteIndex === activeMoveIndex ? ' current-move' : ''}`}
                  {...cellProps(whiteIndex)}
                >
                  {formatMoveNotation(white, variant)}
                </td>
                {black ? (
                  <td
                    className={`move-list-move${blackIndex === activeMoveIndex ? ' current-move' : ''}`}
                    {...cellProps(blackIndex)}
                  >
                    {formatMoveNotation(black, variant)}
                  </td>
                ) : (
                  <td className="move-list-move" />
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default memo(LocalMoveList);
